import { createReducer, AnyAction } from '@reduxjs/toolkit';
import { invoiceMetadata as invoiceMetadataState } from '../state/invoice-metadata';
import {
  cancelChargeEdit,
  providerSearch,
  removeProvider,
  saveInvoice,
  addProvider,
  billingAreaOptions,
  removeAdditionPerformer,
  removeAdditionReferringPerformer,
  addAdditionPerformer,
  addAdditionReferringPerformer,
  changePerformDate,
  createInvoice,
  getInvoiceOptions,
  departmentChange,
  feeScheduleChange
} from '../actions/invoice-editor.actions';
import * as commonEnums from '../../../enums/index';
import { convertRecordToOptionList } from '../../object-utils';
import {
  getDepartmentFromCode,
  getDefaultDepartment
} from '../utils/invoice-utilities';
import { resetCommonState } from '../../../actions/ui.actions';
import { cloneDeep } from '../../../core/object-utils';
import { providerSearchCache } from '../typeahead-cache/caches';

const { ProviderType } = commonEnums;

const formErrors = {
  status: '',
  message: null,
  errors: [],
  fieldErrors: {}
};
/**
 * Returns information about a provider.
 *
 * @param {CMxAPI.Provider} provider
 * @returns {Object} An object containing provider information.
 */
export const getProviderInfo = provider => {
  const [identifier] = provider.identifiers;
  return {
    number: identifier.idNumber,
    issuer: identifier.idTypeCode,
    type: identifier.identifierType
  };
};

const invoiceMetadata = createReducer(invoiceMetadataState, builder => {
  builder.addCase(resetCommonState.type, () => ({ ...invoiceMetadataState }));

  builder.addCase(providerSearch.pending, (state, action) => {
    const codeQuery: CMxCommonApp.LookupQuery = action.meta.arg;
    const meetsMinimum = codeQuery.context.meetsMinimum;
    const providerType = codeQuery?.context?.providerType;
    const isBillingProvideSearch =
      providerType === commonEnums.ProviderType.BILLING_PROVIDER;
    const isPerformingProviderSearch =
      providerType === commonEnums.ProviderType.PERFORMING_PROVIDER;
    const isReferringProviderSearch =
      providerType === commonEnums.ProviderType.REFERRING_PROVIDER;

    const billingProviderSearchResults = isBillingProvideSearch
      ? meetsMinimum
        ? state.billingProviderSearchResults
        : []
      : state.billingProviderSearchResults;

    const referringProviderSearchResults = isReferringProviderSearch
      ? meetsMinimum
        ? state.referringProviderSearchResults
        : []
      : state.referringProviderSearchResults;

    const performingProviderSearchResults = isPerformingProviderSearch
      ? meetsMinimum
        ? state.performingProviderSearchResults
        : []
      : state.performingProviderSearchResults;

    return {
      ...state,
      billingProviderSearchResults,
      referringProviderSearchResults,
      performingProviderSearchResults
    };
  });

  builder.addCase(providerSearch.fulfilled, (state, action) => {
    const providerResults = action.payload;
    const providerType = action.meta.arg?.context?.providerType;
    const isBillingProvideSearch =
      providerType === ProviderType.BILLING_PROVIDER;
    const isPerformingProviderSearch =
      providerType === ProviderType.PERFORMING_PROVIDER;
    const isReferringProviderSearch =
      providerType === ProviderType.REFERRING_PROVIDER;

    const fromCache = providerSearchCache.getLatest(providerResults);

    const billingProviderSearchResults = isBillingProvideSearch
      ? fromCache.providers
      : state.billingProviderSearchResults;
    const referringProviderSearchResults = isReferringProviderSearch
      ? fromCache.providers
      : state.referringProviderSearchResults;
    const performingProviderSearchResults = isPerformingProviderSearch
      ? fromCache.providers
      : state.performingProviderSearchResults;
    return {
      ...state,
      billingProviderSearchResults,
      referringProviderSearchResults,
      performingProviderSearchResults
    };
  });

  builder.addCase(providerSearch.rejected, (state, action) => {
    const providerType = action.meta.arg?.context?.providerType;
    const isBillingProvideSearch =
      providerType === ProviderType.BILLING_PROVIDER;
    const isPerformingProviderSearch =
      providerType === ProviderType.PERFORMING_PROVIDER;
    const isReferringProviderSearch =
      providerType === ProviderType.REFERRING_PROVIDER;

    return {
      ...state,
      billingProviderSearchResults: isBillingProvideSearch
        ? []
        : state.billingProviderSearchResults,
      referringProviderSearchResults: isReferringProviderSearch
        ? []
        : state.referringProviderSearchResults,
      performingProviderSearchResults: isPerformingProviderSearch
        ? []
        : state.performingProviderSearchResults
    };
  });

  builder.addCase(saveInvoice.fulfilled, state => {
    // retain organization and processInstance context as this will
    // not change across invoice editing.
    const { organization, processInstance } = state;
    return {
      ...invoiceMetadataState,
      organization,
      processInstance,
      displayMode: null
    };
  });

  builder.addCase('INVOICE_AVAILABLE/fulfilled', (state, action: AnyAction) => {
    const processInstance = action.payload;
    return { ...state, processInstance };
  });

  builder.addCase(addProvider.type, (state, action: AnyAction) => {
    const provider = action.payload;
    const invoice = { ...state.invoice };
    if (provider) {
      invoice.billing_provider_name = provider.displayName;
      invoice.billing_provider_number = getProviderInfo(provider);
      //defaulted to first role of the selected provider for the time being
      invoice.billing_provider_type = provider?.specialties?.[0]?.name ?? null;
      invoice.billing_provider_credentials = provider?.degree ?? null;
    }

    return {
      ...state,
      invoice,
      billingProviderSearchResults: []
    };
  });

  builder.addCase(billingAreaOptions.type, (state, action: AnyAction) => {
    return {
      ...state,
      billingAreaOptions: action.payload
    };
  });

  builder.addCase(removeProvider.type, state => {
    const invoice = { ...state.invoice };
    invoice.billing_provider_name = '';
    //@ts-ignore
    invoice.billing_provider_number = null;
    invoice.billing_provider_type = null;
    invoice.billing_provider_credentials = null;

    return {
      ...state,
      invoice
    };
  });

  builder.addCase('SELECT_INVOICE_TO_EDIT', (state, action: AnyAction) => {
    const invoiceToEdit = cloneDeep(action.payload);

    // set fee schedule at invoice level from any not null transaction level
    if (!invoiceToEdit?.fee_schedule) {
      invoiceToEdit.fee_schedule = invoiceToEdit.transactions.find(
        transaction => transaction.fee_schedule !== null
      )?.fee_schedule;
    }

    return {
      ...state,
      displayMode: commonEnums.InvoiceEditorMode.EDITOR,
      invoice: invoiceToEdit,
      formErrors: formErrors
    };
  });

  builder.addCase('SELECT_INVOICE_TO_ADD', (state, action: AnyAction) => {
    const newInvoice = cloneDeep(action.payload);
    const invoiceOptions = cloneDeep(state.invoiceOptions);
    const feeSchedules = convertRecordToOptionList(
      invoiceOptions.summary.fee_schedules
    );

    // set default department to the new invoice
    newInvoice.department = getDefaultDepartment(invoiceOptions);
    newInvoice.department_code = newInvoice.department.code;

    // set default fee_schdule to the new invoice
    if (!newInvoice?.fee_schedule && feeSchedules.length === 1) {
      newInvoice.fee_schedule = feeSchedules[0].value;
    }

    // if there are more than one option, user should to choose
    if (feeSchedules.length > 1) {
      newInvoice.fee_schedule = null;
    }

    return {
      ...state,
      displayMode: commonEnums.InvoiceEditorMode.ADDITION,
      invoice: newInvoice,
      formErrors: formErrors
    };
  });

  //@ts-ignore
  builder.addCase(cancelChargeEdit.type, state => {
    return {
      ...state,
      displayMode: null,
      invoice: null,
      billingProviderSearchResults: [],
      referringProviderSearchResults: [],
      performingProviderSearchResults: [],
      formErrors: formErrors
    };
  });

  builder.addCase('ORGANIZATION_AVAILABLE', (state, action) => {
    //@ts-ignore
    return { ...state, organization: action.payload };
  });

  builder.addCase(createInvoice.fulfilled, state => {
    const { organization, processInstance } = state;
    return {
      ...invoiceMetadataState,
      organization,
      processInstance,
      displayMode: null
    };
  });

  builder.addCase(removeAdditionPerformer.type, state => {
    const invoice = { ...state.invoice };
    if (invoice === null) return { ...state };

    invoice.performing_provider_name = '';
    //@ts-ignore
    invoice.performing_provider_number = null;
    invoice.performing_provider_type = null;
    invoice.performing_provider_credentials = null;

    return {
      ...state,
      invoice
    };
  });

  builder.addCase(removeAdditionReferringPerformer.type, state => {
    const invoice = { ...state.invoice };
    invoice.referring_provider_name = '';
    invoice.referring_provider_number = null;
    invoice.referring_provider_type = null;

    return {
      ...state,
      invoice
    };
  });

  builder.addCase(addAdditionPerformer.type, (state, action: AnyAction) => {
    const provider = action.payload;
    const invoice = { ...state.invoice };
    if (provider) {
      invoice.performing_provider_name = provider.displayName;
      invoice.performing_provider_number = getProviderInfo(provider);
      //defaulted to first role of the selected provider for the time being
      invoice.performing_provider_type =
        provider?.specialties?.[0]?.name ?? null;
      invoice.performing_provider_credentials = provider?.degree ?? null;
    }
    return {
      ...state,
      invoice,
      performingProviderSearchResults: []
    };
  });

  builder.addCase(addAdditionReferringPerformer.type, (state, action: AnyAction) => {
    const provider = action.payload;
    const invoice = { ...state.invoice };
    if (provider) {
      invoice.referring_provider_name = provider.displayName;
      invoice.referring_provider_number = getProviderInfo(provider);
      //defaulted to first role of the selected provider for the time being
      invoice.referring_provider_type =
        provider?.specialties?.[0]?.name ?? null;
    }

    return {
      ...state,
      invoice,
      referringProviderSearchResults: []
    };
  });

  builder.addCase(changePerformDate.type, (state, action: AnyAction) => {
    const invoice = { ...state.invoice };
    invoice.date_of_service = action.payload;
    invoice.datetime_of_service = undefined;

    return {
      ...state,
      invoice
    };
  });

  builder.addCase(getInvoiceOptions.fulfilled, (state, action) => {
    const invoiceOptions = action.payload;
    const invoice = cloneDeep(state.invoice);

    return {
      ...state,
      invoiceOptions,
      invoice
    };
  });

  builder.addCase('ALL_INVOICES/fulfilled', (state, action: AnyAction) => {
    const invoiceOptions = action.payload;
    const invoice = cloneDeep(state.invoice);

    return {
      ...state,
      invoiceOptions,
      invoice
    };
  });

  builder.addCase(departmentChange.type, (state, action: AnyAction) => {
    const invoice = cloneDeep(state.invoice);
    invoice.department_code = action.payload;
    invoice.department = getDepartmentFromCode(
      state.invoiceOptions,
      action.payload
    );
    return {
      ...state,
      invoice
    };
  });

  builder.addCase(feeScheduleChange.type, (state, action: AnyAction) => {
    const invoice = cloneDeep(state.invoice);
    invoice.fee_schedule = action.payload;

    return {
      ...state,
      invoice
    };
  });
});

export { invoiceMetadata };
